import { HorizontalTabs } from "@via/components/tabs";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { useEffect, useMemo } from "react";
import { useAuth } from "@via/hooks/useAuth";
import { SkeletonLoader } from "@via/components/loading/loading.tsx";
import { ORGANIZATION_TABS, ROUTES } from "@via/utils/constants.tsx";
import { PageHeading } from "@via/components/page-heading";
import { useGlobal } from "@via/hooks/useGlobal";
import { useCheckSubscriptionHealth } from "@via/pages/billing/billing.api.tsx";
import OrganizationModalWrapper from "@via/pages/organization/organization-modal-wrapper.tsx";
import { useConfigurations } from "@via/api/configuration";
import {
  canCheckSubscriptionHealth,
  canManageOrganization,
  canViewOrganization,
  organizationMembershipFor,
  rememberOrganization,
} from "@via/hooks/organization-access.ts";

const Organization = () => {
  const navigate = useNavigate();
  const { organizationId = "" } = useParams();
  const { user } = useAuth();
  // Always the organization in the URL. userOrganizations[0] is whichever membership sorts
  // first, and showed its name and tabs on every other organization's pages.
  const membership = organizationMembershipFor(user, organizationId);
  const { setSelectedOrganization, showOrganizationCreateModal } = useGlobal();
  const { data: configurations } = useConfigurations();
  // The health check sits behind the Walkins Welcome flag and answers 404 without it, and it
  // also answers 404 for an organization with no subscription. So it only runs for users with
  // the flag, and a failed check means "no billing gate", never an error page for the section.
  const {
    data: checkSubscriptionHealth,
    isLoading: isCheckSubscriptionHealthLoading,
    isSuccess: isCheckSubscriptionHealthSuccess,
  } = useCheckSubscriptionHealth({
    enabled: canCheckSubscriptionHealth(user),
  });

  useEffect(() => {
    if (!isCheckSubscriptionHealthSuccess) return;

    const isValid =
      checkSubscriptionHealth.activeSubscriptionStatus === "active" ||
      checkSubscriptionHealth.activeSubscriptionStatus === "trialing";
    if (!isValid && membership?.role?.name === "org_admin") {
      // Navigate to billing setup page
      navigate(ROUTES.LOGIN);
    }
  }, [
    isCheckSubscriptionHealthSuccess,
    checkSubscriptionHealth,
    navigate,
    membership?.role?.name,
  ]);

  // A platform admin who is not a member has no membership to name the organization from.
  const organizationName = membership?.name || "";

  useEffect(() => {
    // Members of the URL organization and platform admins get in, as the backend allows;
    // undefined means the user has not loaded yet, so nothing is decided.
    const canView = canViewOrganization(user, organizationId);
    if (canView === false) {
      navigate(`/activity`);
      return;
    }
    if (canView) {
      setSelectedOrganization(
        membership ? { id: membership.id, name: membership.name } : null
      );
      // So the Organization link and /activity come back here after a reload (#1069).
      if (membership) rememberOrganization(user, membership.id);
    }
  }, [user, organizationId]);

  const horizontalTabOptions = useMemo(() => {
    let tabs = ORGANIZATION_TABS;

    if (configurations && !configurations.ENABLE_STRIPE) {
      tabs = tabs.filter(tab => tab.name !== "Invoices");
    }

    if (canManageOrganization(user, organizationId) !== true) {
      return tabs.filter(
        tab =>
          tab.name !== "Users" &&
          tab.name !== "Settings" &&
          tab.name !== "Run Environments" &&
          tab.name !== "Invoices"
      );
    }
    return tabs;
  }, [user, organizationId, configurations]);

  return (
    <main className="via-content-wrapper">
      {isCheckSubscriptionHealthLoading ? (
        <div className="grid grid-cols-1 gap-6 py-6 sm:grid-cols-2 lg:grid-cols-3">
          <SkeletonLoader count={12} />
        </div>
      ) : (
        <>
          <div className="via-page-heading-wrapper">
            <PageHeading title={organizationName} />
          </div>
          <HorizontalTabs options={horizontalTabOptions} />
          <div className="mt-6">
            <Outlet />
            {showOrganizationCreateModal ? <OrganizationModalWrapper /> : null}
          </div>
        </>
      )}
    </main>
  );
};

export default Organization;
